import { fetchOptions } from './csrf'

export interface Announcement {
  id: string
  title: string
  body: string | null
  starts_at: string
  ends_at: string | null
  priority: 'info' | 'warning' | 'critical'
  is_system_wide: boolean
  status: 'active' | 'scheduled' | 'expired'
  created_by_id: string
  created_by_name: string
  target_account_ids: string[]
  target_accounts: { id: string; name: string }[]
  acknowledgment_count: number
  acknowledgments?: { user_id: string; user_name: string; acknowledged_at: string }[]
  created_at: string
  updated_at: string
}

export interface AnnouncementFormData {
  title: string
  body: string
  starts_at: string
  ends_at: string
  priority: 'info' | 'warning' | 'critical'
  is_system_wide: boolean
  target_account_ids?: string[]
}

export const announcementsApi = {
  async getAnnouncements(status?: string): Promise<Announcement[]> {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    const qs = params.toString()
    const response = await fetch(`/api/v1/staff/announcements${qs ? `?${qs}` : ''}`, fetchOptions())
    if (!response.ok) {
      throw new Error('Failed to fetch announcements')
    }
    const data = await response.json()
    return data.announcements
  },

  async getAnnouncement(id: string): Promise<Announcement> {
    const response = await fetch(`/api/v1/staff/announcements/${id}`, fetchOptions())
    if (!response.ok) {
      throw new Error('Failed to fetch announcement')
    }
    const data = await response.json()
    return data.announcement
  },

  async createAnnouncement(data: AnnouncementFormData): Promise<Announcement> {
    const response = await fetch('/api/v1/staff/announcements', {
      ...fetchOptions('POST'),
      body: JSON.stringify({ announcement: data }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ errors: ['Failed to create announcement'] }))
      throw new Error(err.errors?.[0] || 'Failed to create announcement')
    }
    const result = await response.json()
    return result.announcement
  },

  async updateAnnouncement(id: string, data: Partial<AnnouncementFormData>): Promise<Announcement> {
    const response = await fetch(`/api/v1/staff/announcements/${id}`, {
      ...fetchOptions('PATCH'),
      body: JSON.stringify({ announcement: data }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ errors: ['Failed to update announcement'] }))
      throw new Error(err.errors?.[0] || 'Failed to update announcement')
    }
    const result = await response.json()
    return result.announcement
  },

  async deleteAnnouncement(id: string): Promise<void> {
    const response = await fetch(`/api/v1/staff/announcements/${id}`, fetchOptions('DELETE'))
    if (!response.ok) {
      throw new Error('Failed to delete announcement')
    }
  },
}
